// Port of ios/Sources/ViewModels/ComposeFormatting.swift.
//
// The formatting toolbar's text surgery: wrapping (and unwrapping) a selection in Markdown
// delimiters. Android's `ComposeFormatting`, rule for rule, pure over (text, selection) so the
// interesting cases — unwrap, empty selection, whitespace-edged selections — live in tests.
//
// Offsets are UTF-16 code units (what `NSTextView.selectedRange()` speaks — and what a
// `<textarea>`'s `selectionStart`/`selectionEnd` speak), so JS string indices map one to one.

export type Format = 'bold' | 'italic' | 'strikethrough' | 'code';

export const allFormats: readonly Format[] = ['bold', 'italic', 'strikethrough', 'code'];

export function delimiter(format: Format): string {
  switch (format) {
    case 'bold':
      return '**';
    case 'italic':
      return '_';
    case 'strikethrough':
      return '~~';
    case 'code':
      return '`';
  }
}

export interface Applied {
  text: string;
  selectionStart: number;
  selectionEnd: number;
}

const WHITESPACE = /\s/u;

/**
 * Applies `format` to the selection:
 * - a selection already wrapped in the delimiter is UNWRAPPED — the button toggles, because
 *   a toggle is what the bold button has meant in every editor since 1985;
 * - an unwrapped selection is wrapped, with edge whitespace moved OUTSIDE the delimiters
 *   (`** hola **` does not parse as bold in Markdown — `**hola**` does);
 * - an empty selection inserts the delimiter pair and parks the cursor inside it.
 *
 * The returned selection always covers the (un)wrapped inner text, so repeated presses
 * toggle cleanly instead of nesting.
 */
export function toggle(text: string, selectionStart: number, selectionEnd: number, format: Format): Applied {
  const start = Math.max(0, Math.min(selectionStart, text.length));
  const end = Math.max(start, Math.min(selectionEnd, text.length));
  const delim = delimiter(format);
  const delimiterLength = delim.length;

  if (start === end) {
    const inserted = text.slice(0, start) + delim + delim + text.slice(start);
    const cursor = start + delimiterLength;
    return { text: inserted, selectionStart: cursor, selectionEnd: cursor };
  }

  const selected = text.slice(start, end);

  // Unwrap when the delimiters sit just OUTSIDE the selection…
  const outsideStart = start - delimiterLength;
  const outsideEnd = end + delimiterLength;
  if (
    outsideStart >= 0 &&
    outsideEnd <= text.length &&
    text.slice(outsideStart, start) === delim &&
    text.slice(end, outsideEnd) === delim
  ) {
    const newText = text.slice(0, outsideStart) + selected + text.slice(outsideEnd);
    return { text: newText, selectionStart: outsideStart, selectionEnd: outsideStart + selected.length };
  }
  // …or just INSIDE it (the user selected the delimiters too).
  if (selected.length >= delimiterLength * 2 && selected.startsWith(delim) && selected.endsWith(delim)) {
    const inner = selected.slice(delimiterLength, selected.length - delimiterLength);
    const newText = text.slice(0, start) + inner + text.slice(end);
    return { text: newText, selectionStart: start, selectionEnd: start + inner.length };
  }

  // Wrap, keeping edge whitespace outside the delimiters: `** hola **` is not bold.
  // Pure-whitespace selections are a no-op FIRST — otherwise leading and trailing both
  // consume the entire selection and the arithmetic below inverts its bounds.
  if (selected.trim() === '') {
    return { text, selectionStart: start, selectionEnd: end };
  }
  let leadingLength = 0;
  while (leadingLength < selected.length && WHITESPACE.test(selected[leadingLength])) leadingLength++;
  let trailingLength = 0;
  while (trailingLength < selected.length && WHITESPACE.test(selected[selected.length - 1 - trailingLength])) {
    trailingLength++;
  }
  const leading = selected.slice(0, leadingLength);
  const trailing = selected.slice(selected.length - trailingLength);
  const core = selected.slice(leadingLength, selected.length - trailingLength);
  const wrapped = leading + delim + core + delim + trailing;
  const newText = text.slice(0, start) + wrapped + text.slice(end);
  const innerStart = start + leadingLength + delimiterLength;
  return { text: newText, selectionStart: innerStart, selectionEnd: innerStart + core.length };
}

// MARK: - Lists (2026-10-14, every client: Swift / Kotlin / C# twins, same rules, same tests)

/** What a new bullet starts with. */
export const bulletMarker = '- ';

const isBreak = (c: string) => c === '\n' || c === '\r';

function lineStartOf(text: string, index: number): number {
  let i = index;
  while (i > 0 && !isBreak(text[i - 1])) i--;
  return i;
}

function lineEndOf(text: string, index: number): number {
  let i = index;
  while (i < text.length && !isBreak(text[i])) i++;
  return i;
}

/**
 * The line's indent (spaces/tabs before the marker) and whether it is a bullet: "- ", "* " or
 * "+ " right after the indent — what Markdown (and Mattermost) draws as a list item.
 */
function bulletOf(text: string, lineStart: number, lineEnd: number): { indent: number; isBullet: boolean } {
  let i = lineStart;
  while (i < lineEnd && (text[i] === ' ' || text[i] === '\t')) i++;
  const isBullet = i + 1 < lineEnd && (text[i] === '-' || text[i] === '*' || text[i] === '+') && text[i + 1] === ' ';
  return { indent: i - lineStart, isBullet };
}

/**
 * The «Lista con viñetas» button: every line the selection touches (the caret's line when
 * nothing is selected) becomes a bullet; when they ALL already are, the bullets come off
 * (toggle, like the other buttons). Blank lines inside a multi-line selection stay blank. A
 * caret keeps its place in the text; a selection grows to cover the whole lines, so pressing
 * again toggles the same lines back.
 */
export function toggleBullets(text: string, selectionStart: number, selectionEnd: number): Applied {
  const start = Math.max(0, Math.min(selectionStart, text.length));
  const end = Math.max(start, Math.min(selectionEnd, text.length));
  // A selection that ends right after a line break does not touch the next line.
  const last = end > start && end > 0 && isBreak(text[end - 1]) ? end - 1 : end;
  const lastEnd = lineEndOf(text, last);
  const lines: Array<{ start: number; end: number }> = [];
  let at = lineStartOf(text, start);
  for (;;) {
    const lineEnd = lineEndOf(text, at);
    lines.push({ start: at, end: lineEnd });
    if (lineEnd >= lastEnd || lineEnd >= text.length) break;
    at = lineEnd + (text[lineEnd] === '\r' && text[lineEnd + 1] === '\n' ? 2 : 1);
  }
  const targets = lines.length === 1 ? lines : lines.filter((l) => text.slice(l.start, l.end).trim() !== '');
  if (targets.length === 0) return { text, selectionStart: start, selectionEnd: end };
  const remove = targets.every((l) => bulletOf(text, l.start, l.end).isBullet);

  const edits: Array<{ at: number; removed: number; inserted: string }> = [];
  for (const l of targets) {
    const { indent, isBullet } = bulletOf(text, l.start, l.end);
    if (remove) edits.push({ at: l.start + indent, removed: 2, inserted: '' });
    else if (!isBullet) edits.push({ at: l.start + indent, removed: 0, inserted: bulletMarker });
  }
  // From the END backwards, so earlier offsets stay valid; a position maps through each.
  let result = text;
  for (let k = edits.length - 1; k >= 0; k--) {
    const e = edits[k];
    result = result.slice(0, e.at) + e.inserted + result.slice(e.at + e.removed);
  }
  const map = (p: number) => {
    let shift = 0;
    for (const e of edits) {
      if (p < e.at) break;
      shift += p >= e.at + e.removed ? e.inserted.length - e.removed : e.at - p + e.inserted.length;
    }
    return p + shift;
  };
  if (start === end) {
    const caret = map(start);
    return { text: result, selectionStart: caret, selectionEnd: caret };
  }
  // No edit sits before the first line's start: it stays where it was.
  return { text: result, selectionStart: lines[0].start, selectionEnd: map(lines[lines.length - 1].end) };
}

/**
 * A line break typed with the newline chord (Shift+Enter / Ctrl+Enter on a desktop, return on a
 * phone's keyboard), replacing the selection. On a bullet the list goes on: the new line starts
 * with the same indent and marker. On an EMPTY bullet the list ends instead: the marker comes off
 * and no line is added (the word processors' rule). Anywhere else it is a plain line break.
 */
export function newline(text: string, selectionStart: number, selectionEnd: number, br = '\n'): Applied {
  const start = Math.max(0, Math.min(selectionStart, text.length));
  const end = Math.max(start, Math.min(selectionEnd, text.length));
  const lineStart = lineStartOf(text, start);
  const lineEnd = lineEndOf(text, start);
  const { indent, isBullet } = bulletOf(text, lineStart, lineEnd);
  const contentStart = lineStart + indent + 2;
  if (isBullet && start >= contentStart) {
    if (start === end && text.slice(contentStart, lineEnd).trim() === '') {
      // The empty bullet: it ends the list (the line stays, now empty).
      return { text: text.slice(0, lineStart) + text.slice(lineEnd), selectionStart: lineStart, selectionEnd: lineStart };
    }
    const inserted = br + text.slice(lineStart, lineStart + indent) + text[lineStart + indent] + ' ';
    const caret = start + inserted.length;
    return { text: text.slice(0, start) + inserted + text.slice(end), selectionStart: caret, selectionEnd: caret };
  }
  const caret = start + br.length;
  return { text: text.slice(0, start) + br + text.slice(end), selectionStart: caret, selectionEnd: caret };
}
